import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  Tooltip,
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";

/* Prefixo do localStorage usado para persistir a preferência "não mostrar
   mais" por usuário. Atrelar ao usuário evita que a escolha de um
   operador "vaze" para outro que use o mesmo computador. */
const STORAGE_PREFIX = "ikaro_dnsa_";

/**
 * Resolve o "id estável" do usuário atual para chavear preferências locais.
 * Preferimos o email (presente em todas as nossas autenticações), com
 * fallback para `id`/`username`/literal "anon". Mantemos como função pura
 * (sem hooks) para poder ser usada também fora de componentes.
 */
function resolveUserKey(user) {
  if (!user) return "anon";
  return (
    user.email ||
    user.id ||
    user.user_id ||
    user.username ||
    user.name ||
    "anon"
  );
}

/**
 * Lê a preferência "não mostrar mais" para uma chave + usuário. Resiliente
 * a navegadores em modo privado / localStorage indisponível.
 */
export function isDoNotShowAgain(preferenceKey, userKey) {
  if (!preferenceKey || !userKey) return false;
  try {
    return (
      window.localStorage.getItem(
        `${STORAGE_PREFIX}${userKey}::${preferenceKey}`,
      ) === "1"
    );
  } catch (_) {
    return false;
  }
}

function setDoNotShowAgain(preferenceKey, userKey, value) {
  if (!preferenceKey || !userKey) return;
  try {
    const fullKey = `${STORAGE_PREFIX}${userKey}::${preferenceKey}`;
    if (value) {
      window.localStorage.setItem(fullKey, "1");
    } else {
      window.localStorage.removeItem(fullKey);
    }
  } catch (_) {
    /* ignora — preferência é "best effort" */
  }
}

/**
 * Hook auxiliar para o consumidor (a página/componente que dispara o
 * popup) verificar se a confirmação deve ser pulada para o usuário atual.
 *
 * Uso típico:
 *
 *   const { shouldSkip } = useConfirmPreference("transcription:markReviewed");
 *   const handleClick = () => {
 *     if (shouldSkip()) doAction();
 *     else setDialogOpen(true);
 *   };
 */
export function useConfirmPreference(preferenceKey) {
  const { user } = useAuth();
  const userKey = resolveUserKey(user);
  return {
    shouldSkip: () => isDoNotShowAgain(preferenceKey, userKey),
    userKey,
  };
}

/**
 * Diálogo de confirmação genérico em estilo Ikaro/Material — substitui o
 * componente legado `PopUp` para os casos em que precisamos de:
 *   - header com título + botão de fechar (X) no canto superior direito;
 *   - área de mensagem clara;
 *   - checkbox "não mostrar mais esta mensagem" persistido por usuário;
 *   - botões padronizados de cancelar/confirmar.
 *
 * Props:
 *   - `open`            boolean
 *   - `title`           string (header)
 *   - `message`         string OU React node (corpo da mensagem)
 *   - `onClose`         () => void
 *   - `onConfirm`       () => void
 *   - `confirmLabel`    string (default: t("confirm"))
 *   - `cancelLabel`     string (default: t("cancel"))
 *   - `confirmColor`    "primary" | "secondary" (default: "primary")
 *   - `confirmIcon`     React node opcional para `startIcon` do botão
 *   - `preferenceKey`   string opcional — quando presente, mostra o
 *                       checkbox "não mostrar mais" e persiste em
 *                       localStorage por usuário (ver `useConfirmPreference`).
 */
function ConfirmDialog({
  open,
  title,
  message,
  onClose,
  onConfirm,
  confirmLabel,
  cancelLabel,
  confirmColor = "primary",
  confirmIcon,
  preferenceKey,
}) {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const userKey = resolveUserKey(user);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleConfirm = () => {
    if (preferenceKey && dontShowAgain) {
      setDoNotShowAgain(preferenceKey, userKey, true);
    }
    onConfirm?.();
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      className="ikaro-confirm-dialog"
    >
      <Box className="ikaro-confirm-dialog__header">
        <Typography
          variant="subtitle1"
          className="ikaro-confirm-dialog__title"
        >
          {title}
        </Typography>
        <Tooltip title={t("close")}>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t("close")}
            className="ikaro-confirm-dialog__close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <DialogContent className="ikaro-confirm-dialog__content">
        {typeof message === "string" ? (
          <Typography variant="body2" className="ikaro-confirm-dialog__text">
            {message}
          </Typography>
        ) : (
          message
        )}
        {preferenceKey ? (
          <FormControlLabel
            className="ikaro-confirm-dialog__dnsa"
            control={
              <Checkbox
                color="primary"
                size="small"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
            }
            label={t("doNotShowAgain")}
          />
        ) : null}
      </DialogContent>
      <DialogActions className="ikaro-confirm-dialog__actions">
        <Button onClick={onClose} variant="outlined">
          {cancelLabel || t("cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          color={confirmColor}
          variant="contained"
          startIcon={confirmIcon || undefined}
        >
          {confirmLabel || t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
